import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { DynamicListWidgetConfig } from "@/models/dashboard/WidgetConfig";
import { useItemService } from "./useServices";
import { PATH_KEYS } from "@/services/appServices";
import type {
  DocumentMetadataFieldInfo,
  WorkspaceDocumentSearchItem,
} from "@/services/workspaceServices";
import {
  getDbValue,
  getInvalidLabel,
  LegislationSourceKey,
} from "@/config/datasources";
import type { Legislation } from "@/models/legislation/Legislation";
import type { Pagination, PaginationQuery } from "@/models/base/Pagination";
import type { SourceDictionary } from "@/models/legislation/LegislationFilter";
import {
  resolveValueFromDbColumn,
  humanizeWithMapping,
  renderCellValue,
} from "@/utils/metadataValueResolver";
import { dateValue } from "@/utils/legislationMetadataUtils";
import useLegislationFilters from "./useLegislationFilters";
import { restError } from "@/models/client/RestResponse";

export type { DocumentMetadataFieldInfo };

export interface WidgetDataItem {
  id: string;
  externalId: string;
  title: string;
  type: string;
  datasource: string;
  status: string;
  date: string;
  name?: string;
  workspace?: string;
  createdAt?: string;
  updatedAt?: string;
  workspaceId?: string;
  documentId?: string;
}

// Note: We no longer convert to ColumnFiltersState for source-specific filters
// The backend now handles sourceFilters directly with proper OR logic between sources

// Convert datasource filter values to the source keys the API expects.
// getDbValue can return names that are not DataSource members (e.g. "EURLex"),
// so these are source keys, not enum values.
const convertDatasourcesToSources = (
  datasources: string[] | undefined
): LegislationSourceKey[] => {
  if (!datasources || datasources.length === 0) return [];

  return datasources
    .map((ds) => getDbValue(ds))
    .filter((dbValue): dbValue is LegislationSourceKey => dbValue !== null);
};

// Extract type from legislation using filter configuration
// This matches how the Kilder table extracts dokumenttype
const extractType = (
  legislation: Legislation,
  filterConfig: SourceDictionary | undefined
): string => {
  const datasource = legislation.datasource?.toLowerCase();
  const sourceConfig = filterConfig?.sources?.[datasource];
  const documentTypeFilter = sourceConfig?.filters?.["documentType"];

  if (documentTypeFilter) {
    const raw = resolveValueFromDbColumn(legislation, {
      key: "documentType",
      databaseColumn: documentTypeFilter.databaseColumn,
      valueMapping: documentTypeFilter.valueMapping,
    });
    const humanized = humanizeWithMapping(raw, documentTypeFilter.valueMapping);
    if (humanized !== null && humanized !== undefined) {
      // Capitalize first letter for display
      const str = renderCellValue(humanized);
      return str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : "-";
    }
  }

  // Fallback: try direct lookup in metadata
  const raw = resolveValueFromDbColumn(legislation, {
    key: "documentType",
    databaseColumn: null,
  });
  if (raw && typeof raw === "string") {
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }

  return "-";
};

// Extract status from legislation - uses isCurrent field like the existing table
const extractStatus = (legislation: Legislation): string => {
  const record = legislation as unknown as Record<string, unknown>;
  const isCurrent =
    legislation.isCurrent ??
    (record["is_current"] as boolean | null | undefined) ??
    (record["IsCurrent"] as boolean | null | undefined);
  if (isCurrent === null || isCurrent === undefined) {
    return "-";
  }
  return isCurrent ? "Gældende" : getInvalidLabel(legislation.datasource);
};

// Extract date from legislation - uses publishedOn directly like the existing table
// Handles both camelCase (publishedOn) and snake_case (published_on) field names
const extractDate = (legislation: Legislation): string => {
  // Access raw record for different casing fallbacks
  const record = legislation as unknown as Record<string, unknown>;

  // Try all possible field name variations (TypeScript model, snake_case, PascalCase)
  let publishedOn: string | null | undefined = legislation.publishedOn;

  if (!publishedOn) {
    publishedOn = record["published_on"] as string | null | undefined;
  }
  if (!publishedOn) {
    publishedOn = record["PublishedOn"] as string | null | undefined;
  }

  // Also check if the date might be in a different format (e.g., object with date property)
  if (
    !publishedOn &&
    typeof record["publishedOn"] === "object" &&
    record["publishedOn"] !== null
  ) {
    // Handle potential DateTime object serialization
    const dateObj = record["publishedOn"] as Record<string, unknown>;
    if (typeof dateObj === "string") {
      publishedOn = dateObj;
    }
  }

  if (publishedOn) {
    // Handle ISO date strings (e.g., "2025-01-15T00:00:00Z" or "2025-01-15")
    const formatted = dateValue(publishedOn);
    return formatted || "-";
  }

  return "-";
};

// Transform Legislation to WidgetDataItem
const transformLegislation = (
  legislation: Legislation,
  filterConfig: SourceDictionary | undefined
): WidgetDataItem => ({
  id: legislation.internalId,
  externalId: legislation.externalId,
  title: legislation.title,
  type: extractType(legislation, filterConfig),
  datasource: legislation.datasource,
  status: extractStatus(legislation),
  date: extractDate(legislation),
});

const transformWorkspaceDocument = (
  document: WorkspaceDocumentSearchItem
): WidgetDataItem => ({
  id: document.documentId,
  externalId: document.documentId,
  title: document.name,
  type: document.documentType,
  datasource: "workspace",
  status: "-",
  date: dateValue(document.updatedAt) || "-",
  name: document.name,
  workspace: document.workspaceName,
  createdAt: dateValue(document.createdAt) || "-",
  updatedAt: dateValue(document.updatedAt) || "-",
  workspaceId: document.workspaceId,
  documentId: document.documentId,
});

export interface UseWidgetDataOptions {
  enabled?: boolean;
  pageNumber?: number;
}

export interface WidgetDataResult {
  items: WidgetDataItem[];
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
}

// Map legacy sort values to valid database columns
const mapSortField = (sortBy: string): string => {
  const sortMapping: Record<string, string> = {
    CreatedAt: "published_on",
    UpdatedAt: "published_on",
    Date: "published_on",
    date: "published_on",
  };
  return sortMapping[sortBy] || sortBy;
};

/**
 * Hook to fetch data for a dynamic list widget based on its configuration.
 * Uses the same filter configuration and value extraction as the Kilder table.
 */
export const useWidgetData = (
  config: DynamicListWidgetConfig,
  options: UseWidgetDataOptions = {}
) => {
  const { enabled = true, pageNumber = 1 } = options;
  const legislationService = useItemService((services) => services.legislation);
  const workspaceService = useItemService((services) => services.workspace);

  // Fetch filter configuration to use the same databaseColumn paths as Kilder
  const { useGetFilterConfiguration } = useLegislationFilters();
  const { data: filterConfig } = useGetFilterConfiguration();

  // Map sortBy to valid database column
  const mappedSortBy =
    config.dataSource === "sources"
      ? mapSortField(config.sortBy)
      : config.sortBy;

  // Combine free-text search and tags into a single search string for the legislation API,
  // matching the same join pattern used by GlobalSearchProvider.
  const combinedLegislationSearch = (() => {
    const term = config.searchQuery?.trim() ?? "";
    const tags = (config.searchTags ?? []).join(" ").trim();
    return [term, tags].filter(Boolean).join(" ") || undefined;
  })();

  // Build pagination query from config
  // Note: We don't include filters here when using sourceFilters - they're sent separately
  const paginationQuery: PaginationQuery = {
    pageNumber,
    pageSize: config.pageSize,
    search: combinedLegislationSearch,
    sortBy: mappedSortBy,
    sortAscending: config.sortAscending,
    filters: [], // Filters are now handled via sourceFilters parameter
  };

  // Get datasources for the query (used as fallback if no sourceFilters)
  const sources = convertDatasourcesToSources(config.filters.datasource);

  // Get source-specific filters for the query
  const sourceFilters = config.filters.sourceFilters;

  return useQuery<WidgetDataResult>({
    queryKey: [
      PATH_KEYS.legislation,
      "widget-data",
      config.dataSource,
      config.filters,
      config.searchQuery,
      config.searchTags,
      mappedSortBy,
      config.sortAscending,
      config.pageSize,
      pageNumber,
      filterConfig ? "with-config" : "no-config", // Re-run when config loads
    ],
    queryFn: async () => {
      if (config.dataSource === "workspace-documents") {
        const workspaceResponse =
          await workspaceService.searchWorkspaceDocuments({
            pageNumber,
            pageSize: config.pageSize,
            search: config.searchQuery || undefined,
            sortBy: config.sortBy,
            sortAscending: config.sortAscending,
            workspaceIds:
              config.filters.workspaceIds &&
              config.filters.workspaceIds.length > 0
                ? config.filters.workspaceIds
                : undefined,
            metadataFilters: config.filters.metadataFilters,
          });

        if (workspaceResponse.success) {
          return {
            items: workspaceResponse.value.data.map(transformWorkspaceDocument),
            totalCount: workspaceResponse.value.totalRecords,
            totalPages: workspaceResponse.value.totalPages,
            hasMore: workspaceResponse.value.hasMore,
          };
        }

        throw restError(workspaceResponse);
      }

      const response = await legislationService.getPaginated(
        paginationQuery,
        sources.length > 0 ? sources : undefined,
        sourceFilters
      );

      if (response.success) {
        const pagination: Pagination<Legislation> = response.value;
        return {
          items: pagination.data.map((leg) =>
            transformLegislation(leg, filterConfig)
          ),
          totalCount: pagination.totalCount ?? pagination.totalRecords ?? 0,
          totalPages: pagination.totalPages,
          hasMore: pagination.hasMore,
        };
      }

      throw restError(response);
    },
    enabled,
    staleTime: 30000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch distinct metadata field names and values for documents
 * accessible to the current user, optionally scoped to specific workspaces.
 * Used to populate filter dropdowns in the widget configuration UI.
 */
export const useDocumentMetadata = (workspaceIds?: string[]) => {
  const workspaceService = useItemService((services) => services.workspace);

  return useQuery<DocumentMetadataFieldInfo[]>({
    queryKey: [PATH_KEYS.workspace, "document-metadata", workspaceIds ?? []],
    queryFn: async () => {
      const response = await workspaceService.getDocumentMetadata(workspaceIds);
      if (response.success) {
        return response.value;
      }
      throw restError(response);
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
};
